/**
 * A tab left open across a deploy still holds the OLD index: its route chunks
 * are hashed URLs the new deploy no longer serves, so the next lazy mount
 * rejects with "Failed to fetch dynamically imported module" and the visitor
 * gets an error screen for a page that is perfectly fine. A reload re-fetches
 * the index and every chunk with it, which IS the fix, so the first such
 * failure reloads instead of surfacing.
 *
 * Reloading on a load failure is a loop waiting to happen: a chunk that is
 * genuinely gone (a bad deploy, a blocked CDN) would fail again on the fresh
 * page and reload again, forever. A sessionStorage note keyed by the failing
 * URL breaks it — the second failure of the SAME url takes the normal error
 * path (boundary + report), which is the case a human needs to see. Session,
 * not local, storage: the guard belongs to this tab and dies with it.
 */

const GUARD_PREFIX = 'ml.chunkReload:';

/** How long a reload note counts. The loop it guards against closes in
 * seconds (fail → reload → fail), while the same hashed URL failing again
 * much later is a fresh incident that deserves its own reload attempt. */
const GUARD_TTL_MS = 10 * 60 * 1000;

/** Every browser words a failed module fetch differently, and none of them
 * expose a code — the message is all there is. Lowercased fragments:
 * Chrome/Edge, Safari, Firefox, then Chrome again for the case where the
 * server answers a missing hashed asset with index.html (wrong MIME type),
 * then Vite's preload helper when a lazy route's hashed CSS file is gone,
 * then webpack's own chunk loader for the bundles it still builds. */
const CHUNK_LOAD_MESSAGES = ['failed to fetch dynamically imported module', 'importing a module script failed', 'error loading dynamically imported module', 'failed to load module script', 'unable to preload css', 'loading chunk ', 'loading css chunk ', 'chunkloaderror'];

export function isChunkLoadError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name}: ${error.message}` : typeof error === 'string' ? error : '';
  const lowercased = text.toLowerCase();
  return CHUNK_LOAD_MESSAGES.some((fragment) => lowercased.includes(fragment));
}

/** The chunk URL the browser names in the message, so the guard is per-asset:
 * a second deploy fails a DIFFERENT hashed URL and is allowed its own reload.
 * Messages that carry no URL fall back to the message itself — still stable
 * enough to catch a repeat. */
function failingChunkKey(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return GUARD_PREFIX + (message.match(/https?:\/\/[^\s'")]+/)?.[0] ?? message);
}

/** The tab's session storage, or null when it is unreachable — Safari's
 * private mode and "block cookies" both make the ACCESS itself throw. */
function guardStore(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    const store = window.sessionStorage;
    // Probe it: some browsers hand back an object whose setItem throws.
    store.setItem(GUARD_PREFIX, '1');
    store.removeItem(GUARD_PREFIX);
    return store;
  } catch {
    return null;
  }
}

/** Drop notes older than the TTL so a long-lived tab can't accumulate one per
 * chunk it ever failed to load. */
function pruneExpired(store: Storage, now: number): void {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key?.startsWith(GUARD_PREFIX)) keys.push(key);
  }
  for (const key of keys) {
    const at = Number(store.getItem(key));
    if (!Number.isFinite(at) || now - at >= GUARD_TTL_MS) store.removeItem(key);
  }
}

/**
 * Reload the page once for a chunk that failed to load. Returns true when a
 * reload is under way and the caller must NOT surface or report the error —
 * the page is about to be replaced, and reporting a failure the reload fixes
 * is what buried the real crashes in the issue list.
 *
 * Returns false — meaning "handle this error normally" — for anything that is
 * not a chunk load failure, for the second failure of the same chunk, on the
 * server (no window to reload), and when session storage is unreachable: with
 * no way to remember a reload happened there is no way to stop at one.
 */
export function reloadOnceOnChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  const store = guardStore();
  if (!store) return false;
  const key = failingChunkKey(error);
  const now = Date.now();
  try {
    pruneExpired(store, now);
    if (store.getItem(key) !== null) return false;
    store.setItem(key, String(now));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Wrap a dynamic-import factory (`() => import('./Page')`) so a stale-chunk
 * failure reloads instead of reaching the Suspense boundary. On the reload
 * path the returned promise never settles ON PURPOSE: the caller keeps
 * showing its fallback for the moment the page has left to live, where
 * rejecting would flash the error screen and file the report the reload
 * exists to avoid.
 */
export function withChunkReload<T>(load: () => Promise<T>): () => Promise<T> {
  return () =>
    load().catch((error: unknown) => {
      if (reloadOnceOnChunkLoadError(error)) return new Promise<T>(() => {});
      throw error;
    });
}
